import { OktonErrorState, OktonLoadingState } from '@okton/platform-ui';
import React, { useEffect, useState } from 'react';

import backend from '../../services/backend';

interface ISsoLoginResponse {
  success?: boolean;
  access_token?: string;
  keycloak_access_token?: string;

  usuario?: {
    id?: string;
    name?: string;
    email?: string;
    empresa_id?: string;
    filial_id?: string;
  };

  user?: {
    id?: string;
    name?: string;
    email?: string;
    empresa_id?: string;
    filial_id?: string;
  };

  permissoes?: string[];
}

const SsoCallback: React.FC = () => {
  const [erro, setErro] = useState('');

  useEffect(() => {
    const executar = async () => {
      const params = new URLSearchParams(window.location.search);

      const code = params.get('code');
      const error = params.get('error');
      const returnedState = params.get('state');

      /*
       * Callback do /bootstrap após um login
       * nativo. Nesse caso a autenticação local
       * já foi gravada antes do redirect.
       */
      if (!code && !error) {
        const token = localStorage.getItem('token');

        window.location.replace(token ? '/dashboard' : '/login');

        return;
      }

      const expectedState = sessionStorage.getItem('okton_sso_state');

      /*
       * Remove code/state da barra do navegador
       * assim que forem lidos.
       */
      window.history.replaceState({}, document.title, '/sso/callback');

      if (!expectedState || !returnedState || expectedState !== returnedState) {
        sessionStorage.removeItem('okton_sso_state');

        sessionStorage.setItem('okton_sso_skip_once', '1');

        setErro('Não foi possível validar o retorno do SSO.');

        return;
      }

      sessionStorage.removeItem('okton_sso_state');

      /*
       * Não existe sessão central.
       * Retorna para o formulário nativo
       * sem criar loop.
       */
      if (error === 'login_required') {
        sessionStorage.setItem('okton_sso_skip_once', '1');

        window.location.replace('/login');
        return;
      }

      if (error) {
        sessionStorage.setItem('okton_sso_skip_once', '1');

        setErro('O SSO não conseguiu concluir a autenticação.');

        return;
      }

      if (!code) {
        setErro('Código de autenticação não recebido.');
        return;
      }

      try {
        const response = await backend.post<ISsoLoginResponse>(
          '/auth/sso/exchange',
          { code },
        );

        const payload = response.data;

        const accessToken =
          payload.keycloak_access_token || payload.access_token;

        if (!accessToken) {
          throw new Error('Token de acesso não retornado.');
        }

        const usuario = payload.usuario || payload.user || {};

        const permissoes = payload.permissoes || [];

        localStorage.setItem('token', accessToken);

        /*
         * SSO central é o dono do refresh token.
         * Uma sessão obtida via SSO não grava
         * refresh token no navegador.
         */
        localStorage.removeItem('refreshToken');

        localStorage.setItem('user', JSON.stringify(usuario));

        localStorage.setItem('permissoes', JSON.stringify(permissoes));

        window.location.replace('/dashboard');
      } catch {
        sessionStorage.setItem('okton_sso_skip_once', '1');

        setErro('Não foi possível concluir o login automático.');
      }
    };

    executar();
  }, []);

  return erro ? (
    <OktonErrorState
      title="Não foi possível concluir o acesso"
      description={erro}
      minH="100vh"
    />
  ) : (
    <OktonLoadingState title="Conectando à sua conta Okton..." minH="100vh" />
  );
};

export default SsoCallback;
